import { useCallback, useState } from 'react';

import { CreateBulkPartActionsRequest } from '../../../@types/partAction.type';
import { ServiceNodeAllocationData } from '../../../@types/service.type';
import { TaskTypeCategory, TaskData } from '../../../@types/task.type';
import { useCreateExceptionalPart } from '../../../queries/part-query';
import { useAddBulkPartActions } from '../../../queries/partAction-query';
import { useUpdateServiceAllocation } from '../../../queries/service-query';
import { useGetNextServicesStatuses, useSaveServiceStatus } from '../../../queries/service-status-query';
import { useCreateAndUpdateTask, useGetTaskTypes } from '../../../queries/task-query';

export interface ExceptionalPartInput {
  partTypeId: number;
  serial1: string;
  conditionCode: string;
  serviceId: number;
  partActionType: string;
  imageData: unknown[];
}

export interface PartActionSubmitInput {
  actions: CreateBulkPartActionsRequest[];
  exceptionalPart?: ExceptionalPartInput;
}

export interface TaskSubmitInput {
  taskTypeCode: string;
  taskTypeCategory?: TaskTypeCategory;
  serviceId: number;
  taskData?: TaskData;
  taskId?: number;
  execute?: boolean;
}

export const useServiceDialogs = (
  serviceId: number,
  statusCode: string,
  serviceTypeCode: string,
  onRefresh: () => void,
) => {
  const [lastError, setLastError] = useState<string | null>(null);

  // Status dialog
  const nextStatusesQuery = useGetNextServicesStatuses(statusCode, serviceTypeCode, serviceId);
  const saveStatusMutation = useSaveServiceStatus(serviceId);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [statusSubmitting, setStatusSubmitting] = useState(false);

  const handleOpenStatusDialog = useCallback(() => {
    nextStatusesQuery.refetch();
    setStatusDialogOpen(true);
  }, [nextStatusesQuery]);

  const handleStatusSubmit = useCallback(async (newStatusCode: string, notes: string) => {
    setStatusSubmitting(true);
    try {
      await saveStatusMutation.mutateAsync({ nextStatus: newStatusCode, note: notes });
      setStatusDialogOpen(false);
      onRefresh();
    } catch (err) {
      setLastError(err instanceof Error ? err.message : 'Status change failed');
    } finally {
      setStatusSubmitting(false);
    }
  }, [saveStatusMutation, onRefresh]);

  // Allocation dialog
  const allocationMutation = useUpdateServiceAllocation(serviceId);
  const [allocationDialogOpen, setAllocationDialogOpen] = useState(false);
  const [allocationSubmitting, setAllocationSubmitting] = useState(false);

  const handleOpenAllocationDialog = useCallback(() => {
    setAllocationDialogOpen(true);
  }, []);

  const handleAllocationSubmit = useCallback(async (nodeId: number, allocationTypeCode: string) => {
    setAllocationSubmitting(true);
    try {
      const data: ServiceNodeAllocationData = { serviceId, userDepotAllocationNodeId: nodeId, allocationTypeCode };
      await allocationMutation.mutateAsync(data);
      setAllocationDialogOpen(false);
      onRefresh();
    } catch (err) {
      setLastError(err instanceof Error ? err.message : 'Allocation update failed');
    } finally {
      setAllocationSubmitting(false);
    }
  }, [serviceId, allocationMutation, onRefresh]);

  // Part Action dialog
  const bulkPartActionMutation = useAddBulkPartActions(serviceId);
  const createExceptionalPartMutation = useCreateExceptionalPart();
  const [partActionDialogOpen, setPartActionDialogOpen] = useState(false);
  const [partActionSubmitting, setPartActionSubmitting] = useState(false);

  const handleOpenPartActionDialog = useCallback(() => {
    setPartActionDialogOpen(true);
  }, []);

  const handlePartActionSubmit = useCallback(async (input: PartActionSubmitInput) => {
    setPartActionSubmitting(true);
    try {
      let actions = input.actions;

      // Step 1: Create exceptional part first if needed
      if (input.exceptionalPart) {
        const exceptionalPayload = {
          partTypeId: input.exceptionalPart.partTypeId,
          serial1: input.exceptionalPart.serial1,
          conditionCode: input.exceptionalPart.conditionCode,
          serviceId: input.exceptionalPart.serviceId,
          exception: true,
          isAccepted: false,
          partActionType: input.exceptionalPart.partActionType,
          imageData: input.exceptionalPart.imageData,
          isActive: false,
        };
        const createdPart = await createExceptionalPartMutation.mutateAsync(exceptionalPayload);
        // Inject the created part ID into the action's installedOrMaintainedPartData
        actions = actions.map((a) => ({
          ...a,
          installedOrMaintainedPartData: {
            ...(a.installedOrMaintainedPartData as unknown as Record<string, unknown>),
            partId: (createdPart as Record<string, unknown>).id,
          } as unknown as JSON,
        }));
      }

      // Step 2: Submit the part action
      await bulkPartActionMutation.mutateAsync(actions);
      setPartActionDialogOpen(false);
      onRefresh();
    } catch (err) {
      setLastError(err instanceof Error ? err.message : 'Part action failed');
    } finally {
      setPartActionSubmitting(false);
    }
  }, [bulkPartActionMutation, createExceptionalPartMutation, onRefresh]);

  // Task dialog
  const taskMutation = useCreateAndUpdateTask(serviceId);
  const taskTypesQuery = useGetTaskTypes(serviceId);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [taskSubmitting, setTaskSubmitting] = useState(false);

  const handleOpenTaskDialog = useCallback(() => {
    taskTypesQuery.refetch();
    setTaskDialogOpen(true);
  }, [taskTypesQuery]);

  const handleTaskSubmit = useCallback(async (task: TaskSubmitInput) => {
    setTaskSubmitting(true);
    try {
      await taskMutation.mutateAsync(task);
      setTaskDialogOpen(false);
      onRefresh();
    } catch (err) {
      setLastError(err instanceof Error ? err.message : 'Task creation failed');
    } finally {
      setTaskSubmitting(false);
    }
  }, [taskMutation, onRefresh]);

  return {
    // Error state
    lastError,
    clearError: useCallback(() => setLastError(null), []),

    // Status
    statusDialogOpen,
    statusSubmitting,
    handleOpenStatusDialog,
    handleCloseStatusDialog: useCallback(() => setStatusDialogOpen(false), []),
    handleStatusSubmit,
    nextStatusesQuery,

    // Allocation
    allocationDialogOpen,
    allocationSubmitting,
    handleOpenAllocationDialog,
    handleCloseAllocationDialog: useCallback(() => setAllocationDialogOpen(false), []),
    handleAllocationSubmit,

    // Part Action
    partActionDialogOpen,
    partActionSubmitting,
    handleOpenPartActionDialog,
    handleClosePartActionDialog: useCallback(() => setPartActionDialogOpen(false), []),
    handlePartActionSubmit,

    // Task
    taskDialogOpen,
    taskSubmitting,
    handleOpenTaskDialog,
    handleCloseTaskDialog: useCallback(() => setTaskDialogOpen(false), []),
    handleTaskSubmit,
    taskTypesQuery,
  };
};
